<script setup lang="ts">
import type { CompetitionSnapshot } from './competition-types';
import { challengeCountdown } from './competition.mjs';
import { useBadgeCatalog } from '../creator/useBadgeCatalog';
import CreatorBadge from './ChallengeBadge.vue';
import DailyChallengeBanner from './DailyChallengeBanner.vue';
import MmIcon from '../base/MmIcon.vue';
const props = defineProps<{
  chart?: CompetitionSnapshot;
  compact?: boolean;
  hero?: boolean;
}>();
const { t, locale } = useI18n(),
  localePath = useLocalePath();
const { definitions, apiBase } = useBadgeCatalog(() => props.hero && !!props.chart?.challenge);
const now = ref<number>();
let timer: ReturnType<typeof setInterval> | undefined;
onMounted(() => {
  if (!props.hero) return;
  now.value = Date.now();
  timer = setInterval(() => {
    now.value = Date.now();
  }, 60000);
});
onBeforeUnmount(() => {
  if (timer) clearInterval(timer);
});
const remaining = computed(() =>
  challengeCountdown(props.chart?.challenge?.next_settlement_at, now.value),
);
const rewards = computed(() =>
  Object.entries(props.chart?.challenge?.rewards || {}).map(
    ([place, reward]) => ({
      ...reward,
      place,
      badge: definitions.value.find((badge) => badge.key === reward.badge_key),
    }),
  ),
);
const number = (value: number) =>
  new Intl.NumberFormat(locale.value).format(value);
</script>
<template>
  <DailyChallengeBanner v-if="!hero" :chart="chart" />
  <div
    v-else
    class="daily-challenge-card"
    :class="{ 'is-compact': compact }"
    data-challenge-card
  >
    <div class="challenge-card-heading">
      <MmIcon name="award" :size="compact ? 22 : 28" />
      <component :is="hero ? 'h1' : 'h3'">{{
        t('mm.challenge.title')
      }}</component>
    </div>
    <p v-if="!compact">{{ t('mm.challenge.subtitle') }}</p>
    <div
      v-if="chart?.challenge"
      class="challenge-rewards"
      data-challenge-rewards
    >
      <div v-for="reward in rewards" :key="reward.place">
        <span>{{
          t(
            reward.place === 'top1'
              ? 'mm.challenge.top1'
              : 'mm.challenge.top10',
          )
        }}</span
        ><CreatorBadge
          :src="reward.badge?.icon_url || undefined"
          :api-base="apiBase"
          :icon="reward.badge_key"
          :label="reward.badge?.name || t('creator.badgesTitle')"
        /><strong>{{ reward.badge?.name || t('creator.badgesTitle') }}</strong
        ><small
          v-if="
            typeof reward.points === 'number' && Number.isFinite(reward.points)
          "
          >+{{ number(reward.points) }} {{ t('incInsights.points') }}</small
        >
      </div>
    </div>
    <p v-if="chart?.challenge" class="challenge-countdown">
      <MmIcon name="clock" :size="16" />
      <span v-if="remaining">{{ t('mm.challenge.countdown', remaining) }}</span>
      <span v-else class="skel" aria-hidden="true" />
    </p>
    <NuxtLink
      v-if="!hero"
      :to="localePath('/music/ranking-music_daily_competition')"
      class="btn btn-primary challenge-join"
      >{{ t('mm.challenge.join') }}<MmIcon name="arrowR" :size="18"
    /></NuxtLink>
  </div>
</template>
<style scoped>
.daily-challenge-card {
  display: grid;
  align-content: start;
  gap: var(--s-5);
  padding: var(--s-6);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  background: var(--surface);
  color: var(--text-2);
  min-width: 0;
}
.challenge-card-heading {
  display: flex;
  align-items: center;
  gap: var(--s-3);
  color: var(--text);
}
.challenge-card-heading > :deep(svg),
.challenge-countdown > :deep(svg) {
  flex-shrink: 0;
}
h1 {
  font: 700 var(--t-h1) / 1.25 var(--font-display);
  text-wrap: balance;
}
h3 {
  font: 600 var(--t-h3) / 1.35 var(--font-ui);
  text-wrap: balance;
}
p {
  margin: 0;
  line-height: 1.6;
}
.challenge-rewards {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: stretch;
  gap: var(--s-3);
}
.challenge-rewards > div {
  display: grid;
  align-content: start;
  min-width: 0;
  gap: var(--s-2);
  padding: var(--s-4);
  background: var(--surface-2);
  border-radius: var(--r-md);
}
.challenge-rewards span,
.challenge-rewards small {
  font-size: var(--t-small);
}
.challenge-rewards strong {
  color: var(--text);
  overflow-wrap: anywhere;
}
.challenge-countdown {
  display: flex;
  align-items: center;
  gap: var(--s-2);
  min-height: 24px;
  font-size: var(--t-small);
  font-variant-numeric: tabular-nums;
}
.challenge-countdown .skel {
  width: 180px;
  max-width: 100%;
  height: 18px;
}
.challenge-join {
  justify-self: start;
  max-width: 100%;
  white-space: normal;
  text-align: center;
}
.is-compact {
  gap: var(--s-3);
  padding: var(--s-4);
}
.is-compact h3 {
  font-size: var(--t-body);
}
.is-compact .challenge-rewards > div {
  padding: var(--s-2);
  gap: var(--s-1);
}
.is-compact .challenge-rewards :is(span, small, strong),
.is-compact .challenge-countdown {
  font-size: var(--t-micro);
}
@media (min-width: 768px) {
  .is-compact {
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    grid-template-areas: 'heading join' 'countdown join' 'rewards rewards';
    gap: var(--s-1) var(--s-4);
    padding: var(--s-3);
  }
  .is-compact .challenge-card-heading {
    grid-area: heading;
    align-self: end;
    gap: var(--s-2);
  }
  .is-compact .challenge-countdown {
    grid-area: countdown;
    align-self: start;
    min-height: 0;
    line-height: 1.3;
  }
  .is-compact .challenge-rewards {
    grid-area: rewards;
    margin-top: var(--s-1);
  }
  .is-compact .challenge-rewards > div {
    grid-template-columns: 48px minmax(0, 1fr);
    grid-template-rows: auto auto 1fr;
    column-gap: var(--s-2);
    row-gap: 0;
  }
  .is-compact .challenge-rewards :deep(.creator-badge) {
    grid-column: 1;
    grid-row: 1 / 4;
    width: 48px;
    height: 48px;
    min-height: 48px;
  }
  .is-compact .challenge-rewards :is(span, strong, small):not(.creator-badge) {
    grid-column: 2;
    line-height: 1.3;
  }
  .is-compact .challenge-join {
    grid-area: join;
  }
}
@media (min-width: 1024px) {
  .is-compact {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.7fr) auto;
    grid-template-areas: 'heading rewards join' 'countdown rewards join';
    gap: var(--s-1) var(--s-4);
    padding: var(--s-4);
  }
  .is-compact .challenge-rewards {
    margin-top: 0;
  }
}
@media (max-width: 479px) {
  .daily-challenge-card:not(.is-compact) {
    padding: var(--s-5);
  }
}
</style>
